import React, { useEffect, useState, useCallback } from 'react';
import { apiUrl } from '../utils/api';
import { formatCurrency } from '../utils/numberFormatter';
import {
  Button,
  MenuItem,
  TextField,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Alert,
  Box,
  Chip,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LoadingOverlay from '../components/LoadingOverlay';
import StyledDataGrid from '../components/StyledDataGrid';

interface BudgetDashboardProps {
  projectId: number;
  access?: string;
}

const BudgetDashboard: React.FC<BudgetDashboardProps> = ({ projectId, access = 'none' }) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = MRs, 1 = POs
  const [mrs, setMrs] = useState([]);
  const [pos, setPOs] = useState([]);
  const [mrLoading, setMrLoading] = useState(true);
  const [poLoading, setPoLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalsBySerial, setTotalsBySerial] = useState({});
  const [poTotalsByNumber, setPoTotalsByNumber] = useState({});
  const [confirmDialog, setConfirmDialog] = useState<{open: boolean, action: 'approve' | 'reject' | null, id: string | null, type: 'mr' | 'po' | null, items: any[], loadingItems: boolean}>({
    open: false,
    action: null,
    id: null,
    type: null,
    items: [],
    loadingItems: false
  });
  const [rejectionComment, setRejectionComment] = useState('');
  const [pmItemComments, setPmItemComments] = useState<{[item_no: number]: string}>({});
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info' | 'warning'}>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Company data for document preview + number formatting
  const [companyData, setCompanyData] = useState({
    company_name: '',
    logo_url: '',
    address: '',
    phone: '',
    email: '',
    number_format: '1,234.56'
  });

  // Project info for MR preview (Job Ref / Job Site)
  const [projectInfo, setProjectInfo] = useState({ project_name: '', project_job_reference: '' });

  // Details dialog state
  const [detailsDialog, setDetailsDialog] = useState<{
    open: boolean;
    type: 'mr' | 'po' | null;
    id: string | null;
    data: any;
    items: any[];
    loadingDetails: boolean;
  }>({
    open: false,
    type: null,
    id: null,
    data: null,
    items: [],
    loadingDetails: false
  });

  // Fetch MRs for current project with items to calculate totals
  const fetchMRs = useCallback(() => {
    if (!projectId) return;
    setMrLoading(true);
    fetch(apiUrl(`/api/material-requisitions?project_id=${projectId}`), {
      credentials: 'include'
    })
      .then(r => r.json())
      .then(async rows => {
        // Only show Pending Approval and Approved MRs in the approval page
        const filtered = Array.isArray(rows) ? rows.filter(mr => mr.status === 'Pending Approval' || mr.status === 'Approved') : [];
        setMrs(filtered);
        // Compute totals per MR (same as MaterialRequisitions page)
        const entries = await Promise.all(
          (filtered || []).map(async r => {
            try {
              const res = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(r.mr_serial_no)}/items`), {
                credentials: 'include'
              });
              const items = res.ok ? await res.json() : [];
              const toNum = v => (v === '' || v === null || v === undefined) ? 0 : +v;
              const cost = items.reduce((s, it) => s + toNum(it.item_quantity) * toNum(it.buying_price), 0);
              const bill = items.reduce((s, it) => s + toNum(it.item_quantity) * toNum(it.charging_price), 0);
              const delv = items.reduce((s, it) => s + toNum(it.delivery_charge), 0);
              const totalNoGST = (r.is_chargeable ? (bill + delv) : (cost + delv));
              return [r.mr_serial_no, totalNoGST];
            } catch {
              return [r.mr_serial_no, 0];
            }
          })
        );
        const map = {};
        entries.forEach(([k, v]) => { map[k] = v; });
        setTotalsBySerial(map);
        setMrLoading(false);
      })
      .catch(() => {
        setMrs([]);
        setTotalsBySerial({});
        setMrLoading(false);
      });
  }, [projectId]);

  // Fetch POs for current project (Pending Approval + Approved, exclude Rejected)
  const fetchPOs = useCallback(() => {
    if (!projectId) {
      setPOs([]);
      return;
    }
    setPoLoading(true);
    fetch(apiUrl(`/api/purchase-orders?project_id=${projectId}`), {
      credentials: 'include'
    })
      .then(r => {
        if (!r.ok) throw new Error('Failed to fetch POs');
        return r.json();
      })
      .then(async rows => {
        console.log('POs fetched:', rows);
        // Only show Pending Approval and Approved in the approval page
        const filteredRows = Array.isArray(rows) ? rows.filter(po => po.status === 'Pending Approval' || po.status === 'Approved') : [];
        setPOs(filteredRows);

        // Calculate totals for each PO (use filtered rows)
        const entries = await Promise.all(
          (filteredRows || []).map(async r => {
            try {
              const res = await fetch(apiUrl(`/api/purchase-orders/${encodeURIComponent(r.po_number)}/items`), {
                credentials: 'include'
              });
              const items = res.ok ? await res.json() : [];
              const toNum = v => (v === '' || v === null || v === undefined) ? 0 : +v;
              // Grand Total = sum(quantity * buying_price + delivery_charge)
              const grandTotal = items.reduce((s, it) => {
                return s + (toNum(it.item_quantity) * toNum(it.buying_price)) + toNum(it.delivery_charge);
              }, 0);
              return [r.po_number, grandTotal];
            } catch {
              return [r.po_number, 0];
            }
          })
        );
        const map = {};
        entries.forEach(([k, v]) => { map[k] = v; });
        setPoTotalsByNumber(map);
        setPoLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching POs:', err);
        setPOs([]);
        setPoTotalsByNumber({});
        setPoLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    fetchMRs();
    fetchPOs();
  }, [fetchMRs, fetchPOs]);

  // Fetch company data for number formatting
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const projectRes = await fetch(apiUrl(`/api/projects/${projectId}`), {
          credentials: 'include'
        });
        if (!projectRes.ok) return;
        const currentProject = await projectRes.json();
        setProjectInfo({
          project_name: currentProject.project_name || '',
          project_job_reference: currentProject.project_job_reference || ''
        });
        if (!currentProject.company_id) return;

        const response = await fetch(apiUrl(`/api/companies/${currentProject.company_id}`), {
          credentials: 'include'
        });
        if (!response.ok) return;
        const data = await response.json();
        setCompanyData({
          company_name: data.company_name || '',
          logo_url: data.logo_url || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          number_format: data.number_format || '1,234.56'
        });
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };
    if (projectId) fetchCompanyData();
  }, [projectId]);

  // Open confirmation dialog
  const handleApproveClick = async (id: string, type: 'mr' | 'po') => {
    setPmItemComments({});
    setConfirmDialog({ open: true, action: 'approve', id, type, items: [], loadingItems: true });
    try {
      const endpoint = type === 'mr'
        ? `/api/material-requisitions/${encodeURIComponent(id)}/items`
        : `/api/purchase-orders/${encodeURIComponent(id)}/items`;
      const res = await fetch(apiUrl(endpoint), { credentials: 'include' });
      const items = res.ok ? await res.json() : [];
      // Pre-fill existing pm_comments
      const existing: {[item_no: number]: string} = {};
      (items || []).forEach((it: any) => { existing[it.item_no] = it.pm_comment || ''; });
      setPmItemComments(existing);
      setConfirmDialog(prev => ({ ...prev, items, loadingItems: false }));
    } catch {
      setConfirmDialog(prev => ({ ...prev, items: [], loadingItems: false }));
    }
  };

  const handleRejectClick = async (id: string, type: 'mr' | 'po') => {
    setRejectionComment('');
    setPmItemComments({});
    setConfirmDialog({ open: true, action: 'reject', id, type, items: [], loadingItems: true });
    try {
      const endpoint = type === 'mr'
        ? `/api/material-requisitions/${encodeURIComponent(id)}/items`
        : `/api/purchase-orders/${encodeURIComponent(id)}/items`;
      const res = await fetch(apiUrl(endpoint), { credentials: 'include' });
      const items = res.ok ? await res.json() : [];
      const existing: {[item_no: number]: string} = {};
      (items || []).forEach((it: any) => { existing[it.item_no] = it.pm_comment || ''; });
      setPmItemComments(existing);
      setConfirmDialog(prev => ({ ...prev, items, loadingItems: false }));
    } catch {
      setConfirmDialog(prev => ({ ...prev, items: [], loadingItems: false }));
    }
  };

  // Close dialog
  const handleDialogClose = () => {
    setConfirmDialog({ open: false, action: null, id: null, type: null, items: [], loadingItems: false });
    setRejectionComment('');
    setPmItemComments({});
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Open details dialog and fetch items
  const handleRowClick = async (type: 'mr' | 'po', rowData: any) => {
    const id = type === 'mr' ? rowData.mr_serial_no : rowData.po_number;

    setDetailsDialog({
      open: true,
      type,
      id,
      data: rowData,
      items: [],
      loadingDetails: true
    });

    try {
      // Fetch line items
      const endpoint = type === 'mr'
        ? `/api/material-requisitions/${encodeURIComponent(id)}/items`
        : `/api/purchase-orders/${encodeURIComponent(id)}/items`;

      const response = await fetch(apiUrl(endpoint), { credentials: 'include' });
      const items = response.ok ? await response.json() : [];

      setDetailsDialog(prev => ({
        ...prev,
        items,
        loadingDetails: false
      }));
    } catch (error) {
      console.error('Error fetching items:', error);
      setDetailsDialog(prev => ({
        ...prev,
        items: [],
        loadingDetails: false
      }));
    }
  };

  // Close details dialog
  const handleDetailsDialogClose = () => {
    setDetailsDialog({
      open: false,
      type: null,
      id: null,
      data: null,
      items: [],
      loadingDetails: false
    });
  };

  // Confirm action
  const handleConfirm = async () => {
    if (!confirmDialog.id || !confirmDialog.action || !confirmDialog.type) return;

    try {
      const status = confirmDialog.action === 'approve' ? 'Approved' : 'Rejected';
      const bodyPayload: any = { status };
      if (confirmDialog.action === 'reject' && rejectionComment.trim()) {
        bodyPayload.rejection_comments = rejectionComment.trim();
      }
      if (Object.keys(pmItemComments).length > 0) {
        bodyPayload.item_comments = Object.entries(pmItemComments).map(([item_no, pm_comment]) => ({
          item_no: Number(item_no),
          pm_comment: pm_comment || ''
        }));
      }
      let response;

      if (confirmDialog.type === 'mr') {
        // Handle MR approval/rejection
        response = await fetch(apiUrl(`/api/material-requisitions/${confirmDialog.id}/status`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(bodyPayload)
        });

        if (response.ok) {
          const data = await response.json();
          if (data.po_number) {
            setSnackbar({
              open: true,
              message: `MR approved successfully! PO ${data.po_number} created.`,
              severity: 'success'
            });
          } else if (confirmDialog.action === 'reject') {
            setSnackbar({
              open: true,
              message: 'MR rejected successfully',
              severity: 'info'
            });
          } else {
            setSnackbar({
              open: true,
              message: 'MR status updated successfully',
              severity: 'success'
            });
          }
          fetchMRs();
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to update MR status',
            severity: 'error'
          });
        }
      } else {
        // Handle PO approval/rejection
        response = await fetch(apiUrl(`/api/purchase-orders/${confirmDialog.id}/status`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(bodyPayload)
        });

        if (response.ok) {
          setSnackbar({
            open: true,
            message: confirmDialog.action === 'approve'
              ? 'PO approved successfully'
              : 'PO rejected successfully',
            severity: confirmDialog.action === 'approve' ? 'success' : 'info'
          });
          fetchPOs();
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to update PO status',
            severity: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred',
        severity: 'error'
      });
    } finally {
      handleDialogClose();
    }
  };

  // Filter MRs based on search term
  const filteredMRs = mrs.filter(mr => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      mr.mr_serial_no?.toLowerCase().includes(search) ||
      mr.mr_description?.toLowerCase().includes(search) ||
      mr.vendor_name?.toLowerCase().includes(search) ||
      mr.status?.toLowerCase().includes(search)
    );
  });

  // Filter POs based on search term
  const filteredPOs = pos.filter(po => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      po.po_number?.toLowerCase().includes(search) ||
      po.mr_serial_no?.toLowerCase().includes(search) ||
      po.vendor_name?.toLowerCase().includes(search) ||
      po.status?.toLowerCase().includes(search)
    );
  });

  const getMRTypeColor = (type: string) => {
    switch (type) {
      case 'Contract': return { bg: '#e3f2fd', color: '#1565c0' };
      case 'VO': return { bg: '#fff3e0', color: '#e65100' };
      case 'Design Change': return { bg: '#f3e5f5', color: '#6a1b9a' };
      default: return { bg: '#f5f5f5', color: '#666' };
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      case 'Pending Approval': return 'warning';
      default: return 'default';
    }
  };

  // DataGrid columns definition
  const columns: GridColDef[] = [
    {
      field: 'mr_serial_no',
      headerName: 'MR Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} color="primary">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'po_for',
      headerName: 'PO Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'mr_type',
      headerName: 'MR Type',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => {
        const colors = getMRTypeColor(params.value);
        return (
          <Tooltip title={params.value === 'VO' ? 'Variation Order' : params.value || '-'}>
            <Chip
              label={params.value || '-'}
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{
                bgcolor: colors.bg,
                color: colors.color,
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'mr_for',
      headerName: 'Title',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'mr_description',
      headerName: 'Description',
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'vendor_name',
      headerName: 'Vendor',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'date_of_issue',
      headerName: 'Date Issued',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || 'Pending'}>
          <Chip
            label={params.value || 'Pending'}
            color={getStatusColor(params.value)}
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{ fontWeight: 600 }}
          />
        </Tooltip>
      )
    },
    {
      field: 'grand_total',
      headerName: 'Grand Total',
      flex: 1,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value, row) => {
        const total = totalsBySerial[row.mr_serial_no] ?? 0;
        return formatCurrency(total, row.currency || 'SGD', companyData.number_format);
      },
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleRowClick('mr', params.row)}
              sx={{ padding: 0.5 }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Approve this MR">
            <span>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleApproveClick(params.row.mr_serial_no, 'mr')}
                disabled={params.row.status === 'Approved' || params.row.status === 'Rejected'}
                sx={{ fontSize: 11, textTransform: 'none', py: 0.3, px: 1.5, minWidth: 'auto' }}
              >
                Approve
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Reject this MR">
            <span>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => handleRejectClick(params.row.mr_serial_no, 'mr')}
                disabled={params.row.status === 'Approved' || params.row.status === 'Rejected'}
                sx={{ fontSize: 11, textTransform: 'none', py: 0.3, px: 1.5, minWidth: 'auto' }}
              >
                Reject
              </Button>
            </span>
          </Tooltip>
        </Box>
      )
    }
  ];

  // PO Columns definition
  const poColumns: GridColDef[] = [
    {
      field: 'po_number',
      headerName: 'PO Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} color="primary">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'mr_serial_no',
      headerName: 'MR Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'vendor_name',
      headerName: 'Vendor',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'date_issued',
      headerName: 'Date Issued',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'delivery_date',
      headerName: 'Delivery Date',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'po_currency',
      headerName: 'Currency',
      flex: 0.7,
      minWidth: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || 'SGD'}
        </Typography>
      )
    },
    {
      field: 'grand_total',
      headerName: 'Grand Total',
      flex: 1,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value, row) => {
        const total = poTotalsByNumber[row.po_number] ?? 0;
        return formatCurrency(total, row.po_currency || 'SGD', companyData.number_format);
      },
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || 'Pending'}>
          <Chip
            label={params.value || 'Pending'}
            color={getStatusColor(params.value)}
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{ fontWeight: 600 }}
          />
        </Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleRowClick('po', params.row)}
              sx={{ padding: 0.5 }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Approve this PO">
            <span>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleApproveClick(params.row.po_number, 'po')}
                disabled={params.row.status === 'Approved' || params.row.status === 'Rejected'}
                sx={{ fontSize: 11, textTransform: 'none', py: 0.3, px: 1.5, minWidth: 'auto' }}
              >
                Approve
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Reject this PO">
            <span>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => handleRejectClick(params.row.po_number, 'po')}
                disabled={params.row.status === 'Approved' || params.row.status === 'Rejected'}
                sx={{ fontSize: 11, textTransform: 'none', py: 0.3, px: 1.5, minWidth: 'auto' }}
              >
                Reject
              </Button>
            </span>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <LoadingOverlay loading={activeTab === 0 ? mrLoading : poLoading} size={50} />


      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={
            <span>
              Material Requisitions&nbsp;(
              <span style={{ color: mrs.filter(mr => mr.status === 'Pending Approval').length > 0 ? '#d32f2f' : 'inherit', fontWeight: 700 }}>
                {mrs.filter(mr => mr.status === 'Pending Approval').length}
              </span>
              /{mrs.length})
            </span>
          } />
          <Tab label={
            <span>
              Purchase Orders&nbsp;(
              <span style={{ color: pos.filter(po => po.status === 'Pending Approval').length > 0 ? '#d32f2f' : 'inherit', fontWeight: 700 }}>
                {pos.filter(po => po.status === 'Pending Approval').length}
              </span>
              /{pos.length})
            </span>
          } />
        </Tabs>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <TextField
          fullWidth
          placeholder={activeTab === 0
            ? "Search by MR Number, Description, Vendor, or Status..."
            : "Search by PO Number, MR Number, Vendor, or Status..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          size="small"
        />
      </Box>

      {/* DataGrid - MRs */}
      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          {mrLoading && <LinearProgress />}
          <StyledDataGrid
            rows={filteredMRs}
            columns={columns}
            getRowId={(row) => row.mr_serial_no}
            loading={mrLoading}
            paginationMode="client"
            onRowDoubleClick={(params) => handleRowClick('mr', params.row)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}

      {/* DataGrid - POs */}
      {activeTab === 1 && (
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          {poLoading && <LinearProgress />}
          <StyledDataGrid
            rows={filteredPOs}
            columns={poColumns}
            getRowId={(row) => row.po_number}
            loading={poLoading}
            paginationMode="client"
            onRowDoubleClick={(params) => handleRowClick('po', params.row)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {confirmDialog.type === 'mr'
            ? (confirmDialog.action === 'approve' ? 'Approve Material Requisition' : 'Reject Material Requisition')
            : (confirmDialog.action === 'approve' ? 'Approve Purchase Order' : 'Reject Purchase Order')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {confirmDialog.type === 'mr'
              ? (confirmDialog.action === 'approve'
                  ? 'Approving this MR will automatically create a Purchase Order. You can add PM comments per line item below.'
                  : 'Are you sure you want to reject this MR? It will be sent back to the Site Team as Draft.')
              : (confirmDialog.action === 'approve'
                  ? 'You can add PM comments per line item below before approving.'
                  : 'Are you sure you want to reject this Purchase Order? It will be sent back to QS as Draft.')}
          </DialogContentText>

          {/* PM comments per line item — shown on both approve and reject */}
          {confirmDialog.loadingItems ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : confirmDialog.items.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                Line Items — Add PM Comments (Optional)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                {confirmDialog.items.map((item: any) => (
                  <Box key={item.item_no} sx={{ p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1, border: '1px solid #eee' }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#555', minWidth: 28 }}>#{item.item_no}</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{item.item_description || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        Qty: {item.item_quantity} {item.item_measurement}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {item.buying_price != null ? `$${Number(item.buying_price).toFixed(2)}` : ''}
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="PM Comment"
                      placeholder="Add comment for this item..."
                      value={pmItemComments[item.item_no] || ''}
                      onChange={(e) => setPmItemComments(prev => ({ ...prev, [item.item_no]: e.target.value }))}
                      sx={{ '& .MuiInputBase-input': { color: 'text.primary' } }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}

          {confirmDialog.action === 'reject' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rejection Comments (Overall)"
              placeholder="Provide overall feedback to the QS team about why this was rejected"
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            variant="contained"
            autoFocus
            disabled={confirmDialog.action === 'reject' && !rejectionComment.trim()}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog (View MR/PO Details) — full document preview */}
      <Dialog
        open={detailsDialog.open}
        onClose={handleDetailsDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700}>
              {detailsDialog.type === 'mr' ? 'Material Requisition Preview' : 'Purchase Order Preview'}
            </Typography>
            {detailsDialog.data && (
              <Chip
                label={detailsDialog.data.status || 'N/A'}
                size="small"
                color={getStatusColor(detailsDialog.data.status)}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {detailsDialog.data && (
            <div className="doc-preview" style={{ background: 'white', padding: '24px 28px' }}>

              {/* ── HEADER: Company Logo + Name + Document Title ── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {companyData.logo_url && (
                    <img
                      src={companyData.logo_url}
                      alt={`${companyData.company_name} Logo`}
                      style={{ maxHeight: 60, maxWidth: 100, objectFit: 'contain' }}
                    />
                  )}
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 20 }}>{companyData.company_name || ''}</div>
                    {companyData.address && (
                      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>{companyData.address}</div>
                    )}
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {companyData.phone && <span>Tel: {companyData.phone}</span>}
                      {companyData.email && <span>Email: {companyData.email}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {detailsDialog.type === 'mr' ? (
                    <>
                      <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>MATERIAL REQUISITION<br />FORM</div>
                      <div style={{ color: '#6b7280', fontWeight: 700, fontSize: 13, marginTop: 4 }}>
                        ({detailsDialog.data.is_chargeable ? 'CHARGEABLE' : 'NON-CHARGEABLE'})
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>PURCHASE ORDER</div>
                      <div style={{ color: '#6b7280', fontWeight: 700, fontSize: 13, marginTop: 4 }}>
                        ({detailsDialog.data.is_chargeable ? 'CHARGEABLE' : 'NON-CHARGEABLE'})
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Red divider */}
              <div style={{ height: 3, background: 'rgb(148,0,0)', margin: '10px 0 14px' }} />

              {/* ── META DETAILS GRID ── */}
              {detailsDialog.type === 'mr' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 14 }}>
                  {/* Row 1: Job Ref + Serial No */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '6px 0' }}>
                    <b>Job Ref :</b> <span>{projectInfo.project_job_reference || '-'}</span>
                  </div>
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '6px 0' }}>
                    <b>Serial No :</b> <span>{detailsDialog.data.mr_serial_no || '-'}</span>
                  </div>

                  {/* Row 2: Job Site + Status */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '6px 0' }}>
                    <b>Job Site :</b> <span>{[projectInfo.project_name, detailsDialog.data.mr_description].filter(Boolean).join(' - ') || '-'}</span>
                  </div>
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '6px 0' }}>
                    <b>Status :</b>{' '}
                    <span style={{ fontWeight: 600, color: detailsDialog.data.status === 'Approved' ? '#4caf50' : detailsDialog.data.status === 'Rejected' ? '#f44336' : '#666' }}>
                      {detailsDialog.data.status || '-'}
                    </span>
                  </div>

                  {/* MR Type */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="MR Type"
                      disabled
                      value={detailsDialog.data.mr_type || ''}
                    />
                  </div>

                  {/* MR For (conditional - only when mr_type set) */}
                  {detailsDialog.data.mr_type && (
                    <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`MR For (${detailsDialog.data.mr_type})`}
                        disabled
                        value={detailsDialog.data.mr_for || ''}
                      />
                    </div>
                  )}

                  {/* Vendor */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Vendor"
                      disabled
                      value={detailsDialog.data.vendor_name || ''}
                    />
                  </div>

                  {/* Date of Issue */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Date of Issue"
                      disabled
                      value={detailsDialog.data.date_of_issue || ''}
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>

                  {/* Currency */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Currency"
                      disabled
                      value={detailsDialog.data.currency || ''}
                    />
                  </div>

                  {/* Created By / Ordered By */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Created By"
                      disabled
                      value={detailsDialog.data.ordered_by || detailsDialog.data.created_by || ''}
                    />
                  </div>

                  {/* PO Reference (shown when PO is raised for this MR) */}
                  {detailsDialog.data.po_for && (
                    <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="PO Reference"
                        disabled
                        value={detailsDialog.data.po_for || ''}
                      />
                    </div>
                  )}

                  {/* GST (chargeable only) */}
                  {detailsDialog.data.is_chargeable && (
                    <>
                      <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                        <TextField fullWidth size="small" label="GST %" disabled value={detailsDialog.data.gst || ''} />
                      </div>
                      <div />
                    </>
                  )}
                </div>
              ) : (
                <>
                  {/* PO Details */}
                  <Box sx={{ marginBottom: 3 }}>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>PO Details</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', marginBottom: 2 }}>
                      <TextField label="PO Number" size="small" fullWidth disabled value={detailsDialog.data.po_number || ''} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <TextField select label="Type" disabled value={detailsDialog.data.is_chargeable ? 'chargeable' : 'non-chargeable'} fullWidth>
                        <MenuItem value="non-chargeable">Non-chargeable</MenuItem>
                        <MenuItem value="chargeable">Chargeable</MenuItem>
                      </TextField>
                      <TextField label="Created By" disabled value={detailsDialog.data.created_by || ''} fullWidth />
                      {detailsDialog.data.po_type && (
                        <TextField label="PO Type" disabled value={detailsDialog.data.po_type || ''} fullWidth />
                      )}
                      {detailsDialog.data.po_type && (
                        <TextField label={`PO For (${detailsDialog.data.po_type})`} disabled value={detailsDialog.data.po_for || ''} fullWidth />
                      )}
                      <TextField label="Vendor" disabled value={detailsDialog.data.vendor_name || ''} fullWidth />
                      <TextField label="Currency" disabled value={detailsDialog.data.po_currency || ''} fullWidth />
                      <TextField label="Date of Issue" disabled value={detailsDialog.data.date_issued || ''} InputLabelProps={{ shrink: true }} fullWidth />
                      {detailsDialog.data.mr_for && (
                        <TextField label="MR Reference" disabled value={detailsDialog.data.mr_for || ''} fullWidth />
                      )}
                      {detailsDialog.data.is_chargeable && (
                        <TextField label="GST %" disabled value={detailsDialog.data.gst || ''} fullWidth />
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ marginY: 3 }} />

                  {/* Delivery & Contact */}
                  <Box sx={{ marginBottom: 3 }}>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>Delivery & Contact</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <TextField label="Delivery Venue" disabled value={detailsDialog.data.delivery_venue || ''} fullWidth />
                      <TextField label="Delivery Date" disabled value={detailsDialog.data.delivery_date || ''} InputLabelProps={{ shrink: true }} fullWidth />
                      <TextField label="Recipient" disabled value={detailsDialog.data.delivery_recipient || ''} fullWidth />
                      <TextField label="HP / Contact" disabled value={detailsDialog.data.delivery_hp || ''} fullWidth />
                      <TextField label="Ordered By" disabled value={detailsDialog.data.ordered_by || ''} fullWidth />
                      <TextField label="Approved By" disabled value={detailsDialog.data.approved_by || ''} helperText="Auto-filled when approved" fullWidth />
                      <TextField label="Delivery Terms" disabled value={detailsDialog.data.delivery_terms || ''} fullWidth />
                      <TextField label="Payment Terms" disabled value={detailsDialog.data.payment_terms || ''} fullWidth />
                    </Box>
                  </Box>

                  <Divider sx={{ marginY: 3 }} />

                  {/* Contract Terms (non-chargeable only) */}
                  {!detailsDialog.data.is_chargeable && (
                    <>
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography variant="h6" sx={{ marginBottom: 2 }}>Contract Terms</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <Box sx={{ gridColumn: 'span 2' }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>Contract Period</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <TextField label="From" disabled value={detailsDialog.data.contract_from || ''} InputLabelProps={{ shrink: true }} fullWidth />
                              <TextField label="To" disabled value={detailsDialog.data.contract_to || ''} InputLabelProps={{ shrink: true }} fullWidth />
                            </Box>
                          </Box>
                          <TextField label="DLP / Warranty (months)" disabled value={detailsDialog.data.warranty_months || ''} fullWidth />
                          <TextField label="Maintenance Services" disabled value={detailsDialog.data.maintenance_service || 'NA'} fullWidth />
                        </Box>
                      </Box>
                      <Divider sx={{ marginY: 3 }} />
                    </>
                  )}

                  {/* Notes */}
                  <Box sx={{ marginBottom: 3, ...(!detailsDialog.data.notes ? { '@media print': { display: 'none' } } : {}) }}>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>Notes</Typography>
                    <TextField value={detailsDialog.data.notes || ''} multiline rows={3} fullWidth disabled sx={{ '@media print': { display: 'none' } }} />
                    <Box sx={{ display: 'none', '@media print': { display: 'block', whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.7 } }}>
                      {detailsDialog.data.notes}
                    </Box>
                  </Box>

                  <Divider sx={{ marginY: 3, ...(!detailsDialog.data.notes ? { '@media print': { display: 'none' } } : {}) }} />

                  {/* Terms & Conditions */}
                  <Box sx={{ marginBottom: 3, ...(!detailsDialog.data.terms_and_conditions ? { '@media print': { display: 'none' } } : {}) }}>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>Terms & Conditions</Typography>
                    <TextField value={detailsDialog.data.terms_and_conditions || ''} multiline rows={6} fullWidth disabled sx={{ '@media print': { display: 'none' } }} />
                    <Box sx={{ display: 'none', '@media print': { display: 'block', whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.7 } }}>
                      {detailsDialog.data.terms_and_conditions}
                    </Box>
                  </Box>

                  <Divider sx={{ marginY: 3, ...(!detailsDialog.data.terms_and_conditions ? { '@media print': { display: 'none' } } : {}) }} />

                  {/* Rejection Comments for PO */}
                  {detailsDialog.data.rejection_comments && (
                    <Box sx={{ p: 2.5, mb: 3, bgcolor: '#fff5f5', border: '2px solid #f44336', borderLeft: '6px solid #f44336', borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ color: '#f44336', mt: 0.2, fontSize: 20, fontWeight: 'bold', lineHeight: 1 }}>⚠</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#c62828', mb: 0.5 }}>Manager Rejection Comments</Typography>
                        <Typography variant="body2" sx={{ color: '#7f1d1d', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontWeight: 500 }}>{detailsDialog.data.rejection_comments}</Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {/* ── ITEMS + BOTTOM SECTION ── */}
              {detailsDialog.loadingDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : detailsDialog.type === 'mr' ? (() => {
                const toNum = (v: any) => (v === '' || v === null || v === undefined) ? 0 : +v;
                const chg = detailsDialog.data.is_chargeable;
                const currency = detailsDialog.data.currency || 'SGD';
                const cost = detailsDialog.items.reduce((s: number, it: any) => s + toNum(it.item_quantity) * toNum(it.buying_price), 0);
                const bill = detailsDialog.items.reduce((s: number, it: any) => s + toNum(it.item_quantity) * toNum(it.charging_price), 0);
                const delv = detailsDialog.items.reduce((s: number, it: any) => s + toNum(it.delivery_charge), 0);
                const gstPct = toNum(detailsDialog.data.gst);
                const gstAmt = (chg ? bill : cost) * gstPct / 100;
                const gross = (chg ? bill : cost) + gstAmt + delv;
                return (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, marginTop: 8 }}>Items</div>
                    {detailsDialog.items.length > 0 ? (
                      <>
                        <Box sx={{ width: '100%', mb: 2 }}>
                          <DataGrid
                            rows={detailsDialog.items.map((item: any, idx: number) => ({ ...item, _viewId: item.item_no || idx + 1 }))}
                            columns={[
                              { field: '_viewId', headerName: '#', width: 60, renderCell: (params: GridRenderCellParams) => String(params.value).padStart(2, '0') },
                              { field: 'item_description', headerName: 'Description', flex: 2, minWidth: 180 },
                              { field: 'category_name', headerName: 'Category', width: 120, renderCell: (params: GridRenderCellParams) => params.value || '-' },
                              { field: 'item_quantity', headerName: 'Qty', width: 80, type: 'number', align: 'right', headerAlign: 'right' },
                              { field: 'buying_price', headerName: 'Buying', width: 110, type: 'number', align: 'right', headerAlign: 'right',
                                renderCell: (params: GridRenderCellParams) => formatCurrency(toNum(params.value), currency, companyData.number_format) },
                              ...(chg ? [{ field: 'charging_price', headerName: 'Charging', width: 110, type: 'number' as const, align: 'right' as const, headerAlign: 'right' as const,
                                renderCell: (params: GridRenderCellParams) => formatCurrency(toNum(params.value), currency, companyData.number_format) }] : []),
                              { field: 'delivery_charge', headerName: 'Delivery', width: 100, type: 'number', align: 'right', headerAlign: 'right',
                                renderCell: (params: GridRenderCellParams) => formatCurrency(toNum(params.value), currency, companyData.number_format) },
                              { field: 'pm_comment', headerName: 'PM Comment', flex: 1, minWidth: 120,
                                renderCell: (params: GridRenderCellParams) => (
                                  <span style={{ color: params.value ? '#222' : '#bbb', fontStyle: params.value ? 'normal' : 'italic', fontSize: 13 }}>{params.value || '-'}</span>
                                ) },
                            ] as GridColDef[]}
                            getRowId={(row) => row._viewId}
                            autoHeight
                            disableRowSelectionOnClick
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 }, printOptions: { disableToolbarButton: true }, csvOptions: { disableToolbarButton: true } } }}
                            rowHeight={48}
                            sx={{
                              border: '1px solid', borderColor: 'divider', borderRadius: 2,
                              '& .MuiDataGrid-toolbarContainer': { padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', gap: 1 },
                              '& .MuiDataGrid-cell': { paddingX: 1.5, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.875rem' },
                              '& .MuiDataGrid-row': { '&:nth-of-type(even)': { backgroundColor: 'action.hover', opacity: 0.8 }, '&:hover': { backgroundColor: 'action.hover' } },
                              '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.100', borderBottom: '2px solid', borderColor: 'divider', fontSize: '0.8rem', fontWeight: 700 },
                              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.8rem' },
                              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' }
                            }}
                          />
                        </Box>
                        {detailsDialog.data.note && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Note</Typography>
                            <Typography variant="body2" color="text.secondary">{detailsDialog.data.note}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
                          {chg && (
                            <>
                              <Typography variant="body2">GST {gstPct.toFixed(2)}%: {formatCurrency(gstAmt, currency, companyData.number_format)}</Typography>
                              <Typography variant="subtitle2" fontWeight={700}>GROSS: {formatCurrency(gross, currency, companyData.number_format)}</Typography>
                            </>
                          )}
                          {!chg && (
                            <Typography variant="subtitle2" fontWeight={700}>Grand Total: {formatCurrency(gross, currency, companyData.number_format)}</Typography>
                          )}
                        </Box>
                      </>
                    ) : (
                      <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No items found</Typography>
                    )}

                    {/* ── BOTTOM TABLE ── */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
                      <tbody>
                        <tr>
                          <td style={{ width: '50%', paddingBottom: 12, paddingRight: 8, verticalAlign: 'top' }}>
                            <TextField fullWidth size="small" label="Delivery Venue" disabled value={detailsDialog.data.delivery_venue || ''} />
                          </td>
                          <td style={{ width: '50%', paddingBottom: 12, paddingLeft: 8, verticalAlign: 'top' }}>
                            <TextField fullWidth size="small" label="Delivery Date" disabled value={detailsDialog.data.delivery_date || ''} InputLabelProps={{ shrink: true }} />
                          </td>
                        </tr>
                        <tr>
                          <td style={{ paddingBottom: 12, paddingRight: 8, verticalAlign: 'top' }}>
                            <TextField fullWidth size="small" label="Recipient" disabled value={detailsDialog.data.delivery_recipient || ''} />
                          </td>
                          <td style={{ paddingBottom: 12, paddingLeft: 8, verticalAlign: 'top' }}>
                            <TextField fullWidth size="small" label="HP / Contact" disabled value={detailsDialog.data.delivery_hp || ''} />
                          </td>
                        </tr>
                        <tr>
                          <td style={{ paddingBottom: 12, paddingRight: 8, verticalAlign: 'top' }}>
                            <TextField fullWidth size="small" label="Ordered By" disabled value={detailsDialog.data.ordered_by || ''} />
                          </td>
                          <td style={{ paddingBottom: 12, paddingLeft: 8, verticalAlign: 'top' }}>
                            <TextField fullWidth size="small" label="Approved By" disabled value={detailsDialog.data.approved_by || ''}
                              helperText="Auto-filled when MR is approved from Budget Dashboard" />
                          </td>
                        </tr>
                        {!chg && (
                          <>
                            <tr>
                              <td colSpan={2} style={{ paddingBottom: 12, verticalAlign: 'top' }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>Contract Period</Typography>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <TextField size="small" label="From" disabled value={detailsDialog.data.contract_from || ''} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                  <TextField size="small" label="To" disabled value={detailsDialog.data.contract_to || ''} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ paddingBottom: 12, paddingRight: 8, verticalAlign: 'top' }}>
                                <TextField fullWidth size="small" label="DLP / Warranty (months)" disabled
                                  value={detailsDialog.data.warranty_months || detailsDialog.data.defects_liability_months || ''} />
                              </td>
                              <td style={{ paddingBottom: 12, paddingLeft: 8, verticalAlign: 'top' }}>
                                <TextField fullWidth size="small" label="Maintenance Services" disabled value={detailsDialog.data.maintenance_service || 'NA'} />
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td colSpan={2} style={{ verticalAlign: 'top' }}>
                            <TextField fullWidth size="small" label="Remarks" multiline rows={2} disabled value={detailsDialog.data.remarks || ''} />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                );
              })() : (() => {
                const toNum = (v: any) => (v === '' || v === null || v === undefined) ? 0 : +v;
                const currency = detailsDialog.data?.po_currency || 'SGD';
                const grandTotal = detailsDialog.items.reduce((s: number, it: any) => s + (toNum(it.item_quantity) * toNum(it.buying_price)) + toNum(it.delivery_charge), 0);
                return (
                  <Box>
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>PO Items</Typography>
                    {detailsDialog.items.length > 0 ? (
                      <>
                        <Box sx={{ width: '100%', mb: 2 }}>
                          <DataGrid
                            rows={detailsDialog.items.map((item: any, idx: number) => ({ ...item, _viewId: item.item_no || idx + 1 }))}
                            columns={[
                              { field: '_viewId', headerName: '#', width: 50 },
                              { field: 'item_description', headerName: 'Description', flex: 2, minWidth: 180 },
                              { field: 'category_name', headerName: 'Category', width: 120, renderCell: (params: GridRenderCellParams) => params.value || '-' },
                              { field: 'item_quantity', headerName: 'Qty', width: 70, type: 'number', align: 'right', headerAlign: 'right' },
                              { field: 'item_measurement', headerName: 'UOM', width: 70 },
                              { field: 'buying_price', headerName: 'Buy Price', width: 110, type: 'number', align: 'right', headerAlign: 'right',
                                renderCell: (params: GridRenderCellParams) => formatCurrency(toNum(params.value), currency, companyData.number_format) },
                              { field: 'currency', headerName: 'Curr', width: 60, renderCell: (params: GridRenderCellParams) => params.value || currency },
                              { field: 'charging_price', headerName: 'Chrg Price', width: 110, type: 'number', align: 'right', headerAlign: 'right',
                                renderCell: (params: GridRenderCellParams) => formatCurrency(toNum(params.value), currency, companyData.number_format) },
                              { field: 'delivery_charge', headerName: 'Del Chrg', width: 100, type: 'number', align: 'right', headerAlign: 'right',
                                renderCell: (params: GridRenderCellParams) => formatCurrency(toNum(params.value), currency, companyData.number_format) },
                              { field: 'line_total', headerName: 'Total', width: 110, type: 'number', align: 'right', headerAlign: 'right',
                                valueGetter: (value: any, row: any) => (toNum(row.item_quantity) * toNum(row.buying_price)) + toNum(row.delivery_charge),
                                renderCell: (params: GridRenderCellParams) => formatCurrency(params.value || 0, currency, companyData.number_format) },
                              ...(detailsDialog.items.some((it: any) => it.pm_comment) ? [{
                                field: 'pm_comment', headerName: 'PM Comment', flex: 1, minWidth: 120,
                                renderCell: (params: GridRenderCellParams) => (
                                  <span style={{ color: params.value ? '#222' : '#bbb', fontStyle: params.value ? 'normal' : 'italic', fontSize: 13 }}>{params.value || '-'}</span>
                                )
                              }] : []),
                            ] as GridColDef[]}
                            getRowId={(row) => row._viewId}
                            autoHeight
                            disableRowSelectionOnClick
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 }, printOptions: { disableToolbarButton: true }, csvOptions: { disableToolbarButton: true } } }}
                            rowHeight={48}
                            sx={{
                              border: '1px solid', borderColor: 'divider', borderRadius: 2,
                              '& .MuiDataGrid-toolbarContainer': { padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', gap: 1 },
                              '& .MuiDataGrid-cell': { paddingX: 1.5, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', fontSize: '0.875rem' },
                              '& .MuiDataGrid-row': { '&:nth-of-type(even)': { backgroundColor: 'action.hover', opacity: 0.8 }, '&:hover': { backgroundColor: 'action.hover' } },
                              '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.100', borderBottom: '2px solid', borderColor: 'divider', fontSize: '0.8rem', fontWeight: 700 },
                              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.8rem' },
                              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' }
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Grand Total: {formatCurrency(grandTotal, currency, companyData.number_format)}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No items found</Typography>
                    )}
                  </Box>
                );
              })()}

            </div>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDetailsDialogClose} variant="outlined">Close</Button>
          {detailsDialog.data && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const prev = document.title;
                document.title = detailsDialog.type === 'mr'
                  ? `MR-${detailsDialog.data?.mr_serial_no || 'draft'}`
                  : `PO-${detailsDialog.data?.po_number || 'draft'}`;
                window.print();
                document.title = prev;
              }}
            >
              Print / PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetDashboard;
